/**
 * Advertiser publisher-stats — dash data layer (feature #148).
 *
 * Thin typed wrapper over `apiClient` for the
 * `/api/v1/advertiser-publisher-stats/*` endpoints. Mirrors the response
 * shapes declared in `apps/api/src/routes/advertiser-publisher-stats.ts`:
 *
 *   GET /:advertiser_id?from=<ISO>&to=<ISO>
 *     → { advertiser_id, from, to,
 *         totals: { clicks, conversions, payout_cents, revenue_cents },
 *         publishers: [{ affiliate_id, affiliate_name, clicks, conversions,
 *                        payout_cents, revenue_cents, epc_cents }],
 *         daily:     [{ affiliate_id, date, clicks, conversions,
 *                        payout_cents, revenue_cents }] }
 *
 *   GET /top/:advertiser_id?limit=N
 *     → { advertiser_id, limit,
 *         publishers: [<same shape as above>] }
 *
 * Keep this file dependency-free beyond the root `apiClient` — no React, no
 * TanStack — so the pure validators + formatters can be exercised from a
 * plain vitest file without a DOM.
 */
import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Response shapes — aligned with the API declarations. Re-declared here
// because the dash cannot import across the `apps/` boundary in the 16-
// parallel lane model.
// ---------------------------------------------------------------------------

export interface PublisherAggregate {
	affiliate_id: string;
	affiliate_name: string | null;
	clicks: number;
	conversions: number;
	payout_cents: number;
	revenue_cents: number;
	/** Earnings per click in cents; 0 when clicks === 0. */
	epc_cents: number;
}

export interface DailyRow {
	affiliate_id: string;
	date: string;
	clicks: number;
	conversions: number;
	payout_cents: number;
	revenue_cents: number;
}

export interface RangeResponse {
	advertiser_id: string;
	from: string;
	to: string;
	totals: {
		clicks: number;
		conversions: number;
		payout_cents: number;
		revenue_cents: number;
	};
	publishers: PublisherAggregate[];
	daily: DailyRow[];
}

export interface TopResponse {
	advertiser_id: string;
	limit: number;
	publishers: PublisherAggregate[];
}

// ---------------------------------------------------------------------------
// Validators — strict ISO-date + identifier shapes. Same pattern as feature
// #115 (smartlinks-analytics) so the form layer reads familiar.
// ---------------------------------------------------------------------------

export const ADVERTISER_ID_MAX = 64;
export const ADVERTISER_ID_RE = /^[a-z0-9_-]{1,64}$/i;

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidIsoDate(value: string): boolean {
	const match = ISO_DATE_RE.exec(value);
	if (!match) return false;
	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	if (month < 1 || month > 12) return false;
	if (day < 1 || day > 31) return false;
	const ts = Date.UTC(year, month - 1, day);
	const check = new Date(ts);
	return (
		check.getUTCFullYear() === year &&
		check.getUTCMonth() === month - 1 &&
		check.getUTCDate() === day
	);
}

export function isoDateCompare(a: string, b: string): number {
	return a < b ? -1 : a > b ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Hand-rolled form schema — zod-free, mirrors the other dash features.
// ---------------------------------------------------------------------------

export interface RangeQueryInput {
	advertiserId: string;
	from: string;
	to: string;
}

export interface RangeQueryParsed {
	advertiserId: string;
	from: string;
	to: string;
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const rangeQuerySchema = {
	safeParse(input: RangeQueryInput): ParseResult<RangeQueryParsed> {
		const errors: Record<string, string> = {};

		const advertiserId = (input.advertiserId ?? "").trim();
		if (advertiserId.length === 0) {
			errors.advertiserId = "Advertiser ID is required.";
		} else if (advertiserId.length > ADVERTISER_ID_MAX) {
			errors.advertiserId = `Advertiser ID must be ${ADVERTISER_ID_MAX} chars or fewer.`;
		} else if (!ADVERTISER_ID_RE.test(advertiserId)) {
			errors.advertiserId =
				"Advertiser ID may contain letters, digits, dashes, and underscores.";
		}

		const fromIso = (input.from ?? "").trim();
		if (fromIso.length === 0) {
			errors.from = "Start date is required.";
		} else if (!isValidIsoDate(fromIso)) {
			errors.from = "Start date must be a valid YYYY-MM-DD.";
		}

		const toIso = (input.to ?? "").trim();
		if (toIso.length === 0) {
			errors.to = "End date is required.";
		} else if (!isValidIsoDate(toIso)) {
			errors.to = "End date must be a valid YYYY-MM-DD.";
		}

		if (!errors.from && !errors.to) {
			if (isoDateCompare(fromIso, toIso) > 0) {
				errors.to = "End date must be on or after the start date.";
			}
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}

		return {
			success: true,
			data: { advertiserId, from: fromIso, to: toIso },
		};
	},
};

// ---------------------------------------------------------------------------
// Formatters — the table displays cents as USD and EPC with two decimals.
// ---------------------------------------------------------------------------

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return n.toLocaleString("en-US");
}

export function formatCents(cents: number): string {
	if (!Number.isFinite(cents)) return "$0.00";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const dollars = Math.floor(abs / 100);
	const pennies = abs - dollars * 100;
	return `${sign}$${dollars.toLocaleString("en-US")}.${String(pennies).padStart(2, "0")}`;
}

/**
 * EPC value formatted for the sortable table. Stored as integer cents on the
 * wire so we format identically to `formatCents`.
 */
export function formatEpc(cents: number): string {
	return formatCents(cents);
}

/**
 * Conversion-rate percentage for a row — UI-only derived metric. We render
 * 0% when clicks are zero.
 */
export function computeCrPercent(clicks: number, conversions: number): number {
	if (!Number.isFinite(clicks) || !Number.isFinite(conversions)) return 0;
	if (clicks <= 0) return 0;
	return (conversions / clicks) * 100;
}

export function formatCrPercent(clicks: number, conversions: number): string {
	return `${computeCrPercent(clicks, conversions).toFixed(2)}%`;
}

/**
 * Bar width (as a percentage-of-rail CSS string) for the top-10 chart. The
 * caller passes in `value` (e.g. conversions) and `max` (the largest value in
 * the chart). Protects against divide-by-zero + overflow.
 */
export function barWidth(value: number, max: number): string {
	if (!Number.isFinite(value) || !Number.isFinite(max)) return "0.00%";
	if (max <= 0) return "0.00%";
	const pct = Math.max(0, Math.min(100, (value / max) * 100));
	return `${pct.toFixed(2)}%`;
}

// ---------------------------------------------------------------------------
// Sortable table helpers — the UI sorts by metric columns. Keep the compare
// fns pure so we can unit-test them.
// ---------------------------------------------------------------------------

export type SortKey =
	| "affiliate_name"
	| "clicks"
	| "conversions"
	| "epc_cents"
	| "revenue_cents"
	| "payout_cents";

export type SortDir = "asc" | "desc";

/**
 * Return a copy of `publishers` sorted by `key` in `dir`. Stable on ties
 * (we tie-break on affiliate_id ascending).
 */
export function sortPublishers(
	publishers: PublisherAggregate[],
	key: SortKey,
	dir: SortDir,
): PublisherAggregate[] {
	const out = publishers.slice();
	const multiplier = dir === "asc" ? 1 : -1;

	out.sort((a, b) => {
		let cmp: number;
		if (key === "affiliate_name") {
			const an = (a.affiliate_name ?? a.affiliate_id).toLowerCase();
			const bn = (b.affiliate_name ?? b.affiliate_id).toLowerCase();
			cmp = an < bn ? -1 : an > bn ? 1 : 0;
		} else {
			cmp = a[key] - b[key];
		}
		if (cmp !== 0) return cmp * multiplier;
		return a.affiliate_id < b.affiliate_id ? -1 : 1;
	});

	return out;
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/advertiser-publisher-stats";

/**
 * Fetch the date-range breakdown for an advertiser. 404 normalised to `null`
 * so the UI can show an empty state without a toast.
 */
export async function fetchAdvertiserPublisherRange(args: {
	advertiserId: string;
	from: string;
	to: string;
}): Promise<RangeResponse | null> {
	try {
		return await apiClient.get<RangeResponse>(
			`${BASE}/${encodeURIComponent(args.advertiserId)}`,
			{ query: { from: args.from, to: args.to } },
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

/**
 * Fetch the top-N publishers for an advertiser (ranked by conversions desc).
 * Default and upper bound enforced server-side.
 */
export async function fetchAdvertiserPublisherTop(args: {
	advertiserId: string;
	limit?: number;
}): Promise<TopResponse | null> {
	try {
		const query: Record<string, number> = {};
		if (args.limit !== undefined) query.limit = args.limit;
		return await apiClient.get<TopResponse>(
			`${BASE}/top/${encodeURIComponent(args.advertiserId)}`,
			{ query },
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export const advertiserPublisherStatsQueryKeys = {
	all: ["advertiser-publisher-stats"] as const,
	range: (advertiserId: string, from: string, to: string) =>
		[
			...advertiserPublisherStatsQueryKeys.all,
			"range",
			advertiserId,
			from,
			to,
		] as const,
	top: (advertiserId: string, limit: number) =>
		[
			...advertiserPublisherStatsQueryKeys.all,
			"top",
			advertiserId,
			limit,
		] as const,
};
